import React from "react";
import { JhButton } from "designPackages/buttonPackage";
import { Share2, Rocket, UserRoundPlus } from "lucide-react";
import { useIsMobile } from "../../../hooks/useDeviceType";
import useModalStore from "../../../zustandStores/useModalStore";
import useCreditProfileStore from "../../../zustandStores/userCreditProfileStore";
import useSingleJobSkus from "../../../zustandStores/singleJobSkus";
import { getBoostDetails } from "../../../services/boostUtils";
import JobService from "../../../services/JobService";
import { JhUseToast } from "designPackages/toastPackage";
import LogService from "../../../services/LogService";
import BOOST_EVENTS from "../../../utils/BoostEvents";
import UpgradeToPremiumCTA from "../../UpgradeToPremiumCta";
import JobBoostedCountdown from "./JobBoostedCountdown";
import { JhText } from "designPackages/textPackage";
import useResponseManagerV3PageStore from "../../../zustandStores/response-manager-v3/useResponseManagerV3PageStore";
import { useRouter } from "next/router";
import { Routes } from "routes/RouteConstants";
import {
  JhTooltip,
  JhTooltipContent,
  JhTooltipProvider,
  JhTooltipTrigger
} from "designPackages/toolTip";
import { RESPONSE_MANAGER_V3 } from "constants/responseManagerV3Constants";
import JobBoost from "components/JobBoost";

interface HeaderActionsProps {
  onJobBoostCallback?: () => void;
  onJobUpgradeCallback?: () => void;
  isJobClosed?: boolean;
}

const HeaderActions: React.FC<HeaderActionsProps> = ({
  onJobBoostCallback = () => {},
  onJobUpgradeCallback = () => {},
  isJobClosed = false
}) => {
  const isMobile = useIsMobile();
  const { toast } = JhUseToast();
  const router = useRouter();

  const { setModalData } = useModalStore(state => ({
    setModalData: state.setModalData
  }));

  const { fetchCreditProfile, credtProfileData } = useCreditProfileStore(
    state => ({
      fetchCreditProfile: state.fetchData,
      credtProfileData: state.data
    })
  );

  const { fetchSingleJobCreditMetadata } = useSingleJobSkus((state: any) => ({
    fetchSingleJobCreditMetadata: state.fetchData
  }));

  // Get job data from store
  const { jobData } = useResponseManagerV3PageStore(state => ({
    jobData: state.jobData
  }));

  // Don't render if no job data is available
  if (!jobData) {
    return null;
  }

  const { freeBoostConsumed = false, totalAvailableCredits } =
    credtProfileData || {};
  const { show, availableBoost, boostYetToStart, startsIn, boostLimitReached } =
    getBoostDetails(jobData.boost_details);

  const isBasic = jobData.jobPlan === "BASIC";
  const isPremium = jobData.jobPlan === "PREMIUM";
  const isJobBoosted = jobData?.boost_details?.has_mini_boost;

  const handleShareJob = async () => {
    try {
      const shareData = await JobService.shareJob(
        jobData.job_id,
        RESPONSE_MANAGER_V3
      );

      if (isMobile && shareData?.whatsapp_share_text) {
        const encodedText = encodeURIComponent(shareData.whatsapp_share_text);
        window.open(`https://wa.me/?text=${encodedText}`, "_blank");
        return;
      }

      setModalData({
        state: "open",
        type: "shareJobModal",
        data: {
          jobId: jobData.job_id,
          jobTitle: jobData.job_title,
          shareInfo: shareData,
          loading: false
        }
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate sharing options",
        variant: "destructive"
      });
    }
  };

  // Upgrade Success Callback
  const onJobUpgrade = async () => {
    if (onJobUpgradeCallback) {
      onJobUpgradeCallback();
    }
  };

  const onJobBoost = () => {
    if (onJobBoostCallback) {
      onJobBoostCallback();
    }
  };

  const onViewDatabase = () => {
    router.push(`${Routes.DATABASE_PRODUCT}?job_id=${jobData.job_id}`);
  };

  const renderViewDatabase = () => {
    if (jobData.is_resdex_allowed)
      return (
        <JhButton
          variant="outline"
          size="sm"
          onClick={onViewDatabase}
          className="rounded-2xl pt-2 px-4 gap-1"
          disabled={isJobClosed}
        >
          <UserRoundPlus size={16} className="text-foreground" />
          <JhText variant="subtitle4">View Database</JhText>
        </JhButton>
      );
    return null;
  };

  const renderJobBoostButton = () => {
    const button = (
      <JobBoost
        data={{ source: RESPONSE_MANAGER_V3, jobId: jobData.job_id }}
        callBack={onJobBoost}
        boostDetails={jobData.boost_details}
        renderFunction={({ onBoost }) => (
          <JhButton
            variant="outline"
            size="sm"
            onClick={onBoost}
            disabled={boostYetToStart || isJobClosed}
            className={`flex items-center justify-center rounded-2xl gap-1 ${
              boostLimitReached ? "bg-muted2 text-muted-foreground" : ""
            }`}
          >
            <Rocket
              size={16}
              className={
                boostLimitReached ? "text-muted-foreground" : "text-foreground"
              }
            />
            <JhText variant="subtitle4">Boost Job</JhText>
          </JhButton>
        )}
      />
    );

    let tooltipContent = "Boost your job for 24 hours to reach more candidates";
    if (boostLimitReached) {
      tooltipContent = "You have used all your available boosts for this job";
    } else if (boostYetToStart && startsIn && typeof startsIn === "object") {
      tooltipContent =
        "Boost will be available post 24hrs of upgrading to premium";
    }
    if ((isPremium && show) || boostYetToStart) {
      return (
        <JhTooltipProvider>
          <JhTooltip delayDuration={200}>
            <JhTooltipTrigger asChild>
              <span>{button}</span>
            </JhTooltipTrigger>
            <JhTooltipContent variant="primary" className="max-w-56">
              {tooltipContent}
            </JhTooltipContent>
          </JhTooltip>
        </JhTooltipProvider>
      );
    }
  };

  const renderJobActionCTAs = () => {
    if (isBasic) {
      return (
        <UpgradeToPremiumCTA
          item={{
            job_id: jobData.job_id,
            jobPlan: jobData.jobPlan,
            status: jobData.status?.toUpperCase()
          }}
          view={RESPONSE_MANAGER_V3}
          page={RESPONSE_MANAGER_V3}
          hideSubTag={false}
          onClickHandler={onJobUpgrade}
        />
      );
    }

    if (isJobBoosted && !isJobClosed) {
      return <JobBoostedCountdown />;
    }

    return renderJobBoostButton();
  };

  const renderShareButton = () => {
    return (
      <JhButton
        variant="outline"
        size="sm"
        onClick={handleShareJob}
        className="rounded-full p-2.5"
        disabled={isJobClosed}
      >
        <Share2 size={16} className="text-foreground" />
      </JhButton>
    );
  };

  return (
    <div className="flex items-center gap-3">
      {!isMobile && (
        <>
          {renderShareButton()}
          {renderViewDatabase()}
          {renderJobActionCTAs()}
        </>
      )}
    </div>
  );
};

export default HeaderActions;
